import { useEffect, useState } from "react";
import { useAppSelector } from "app/hooks";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";
import PoiApi from "../../poiApi";
import { PoiCreatePayload } from "../../poiModels";
import { addKrpanoHotspot } from "shared/helpers/krpano";

type UsePoiCreate = () => [
  modalOpen: boolean,
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  loading: boolean,
  actionCallback: () => Promise<void>
];

/**
 * # Create a POI
 */
export const usePoiCreate: UsePoiCreate = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [poiAthAtv, setPoiAthAtv] = useState<Pick<
    PoiCreatePayload,
    "ath" | "atv"
  > | null>(null);
  const guid = useAppSelector((state) => state.panorama.guid);
  const currentFieldId = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.fid
  );
  const droneLat = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.lat
  );
  const droneLng = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.lng
  );
  const droneAltitude = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.drone_altitude
  );
  const orientationAngle = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.orientation_angle
  );

  // This listener is for when krpano requests for a POI to be created
  useEffect(() => {
    const createPoiHandler = async (
      event: WindowEventMap["krpano__poi_create"]
    ) => {
      if (
        notNullNorUndefined(event.detail) &&
        notNullNorUndefined(event.detail.ath) &&
        notNullNorUndefined(event.detail.atv)
      ) {
        const ath = event.detail.ath;
        const atv = event.detail.atv;
        setPoiAthAtv({ ath, atv });
        setModalOpen(true);
      } else {
        // TODO: KrpanoDispatchedEvent Error handling
      }
    };
    window.addEventListener("krpano__poi_create", createPoiHandler);
    return () => {
      window.removeEventListener("krpano__poi_create", createPoiHandler);
    };
  }, []);

  // Add POI
  const poiCreate = async () => {
    if (
      notNullNorUndefined(poiAthAtv) &&
      notNullNorUndefined(droneLat) &&
      notNullNorUndefined(droneLng) &&
      notNullNorUndefined(droneAltitude) &&
      notNullNorUndefined(currentFieldId) &&
      notNullNorUndefined(guid)
    ) {
      setLoading(true);
      const response = await PoiApi.createPoi({
        ath:
          notNullNorUndefined(poiAthAtv.ath) &&
          notNullNorUndefined(orientationAngle)
            ? poiAthAtv.ath + parseFloat(orientationAngle)
            : poiAthAtv.ath,
        atv: poiAthAtv.atv,
        drone_lat: droneLat,
        drone_lon: droneLng,
        drone_alt: droneAltitude,
        fieldId: currentFieldId,
        panoGuid: guid,
      });
      setLoading(false);
      if (response.success && response.data) {
        // Add new hotspot
        const poiNumber = response.data.id;
        const scoutPointName = `poi ${poiNumber}`;
        const isTrialPlot = false;
        addKrpanoHotspot(
          scoutPointName,
          {
            // ATH/ATV
            ath: poiAthAtv.ath,
            atv: poiAthAtv.atv,
            // POI Number
            poi_number: poiNumber,
            // TODO: Check if needed anywhere
            altitude: response.data.height_in_meters,
            // Type
            type: "image",
            // UI/UX Behaviours
            onclick: `Load_POI(null, ${poiNumber});`,
            onover: `set(global.onPoi, ${scoutPointName});`,
            onout: "set(global.onPoi, null);",
            // Group
            group: "poi_spot",
          },
          isTrialPlot ? "POI_TrialPlot_Marker" : "POI_Marker"
        );
        window.krpano.call("scale_hotspots()");
        // TODO: Success handling
        // toast.success("POI created");
        // Close modal
        setModalOpen(false);
      } else {
        // TODO: Error handling
        console.warn("Error creating POI", response.errors);
      }
    } else {
      console.warn("Possible missing data, check if any of these are null", {
        droneLat,
        droneLng,
        droneAltitude,
        currentFieldId,
        guid,
      });
    }
  };

  return [modalOpen, setModalOpen, loading, poiCreate];
};
